import { useEffect, useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  AlertTitle,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { API_ENDPOINT, APP_LOGO_192 } from "../../utils/constants";
import { LoadingButton } from "@mui/lab";
import { useParams } from "react-router-dom";
import axios from "axios";
import { getErrorResponse, toastError } from "../../utils/util";
import { toast } from "react-toastify";

const PasswordSetPage = () => {
  const { id } = useParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState(null);
  const [verificationError, setVerificationError] = useState(null);
  const handlePasswordToggle = () => setShowPassword((prev) => !prev);

  useEffect(() => {
    const verifyToken = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${API_ENDPOINT}user/verify-pwd-token?id=${id}`
        );
        setVerificationMessage(data.message);
      } catch (error) {
        setVerificationError(getErrorResponse(error));
      } finally {
        setLoading(false);
      }
    };
    verifyToken();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match!");
      return;
    }
    try {
      setVerifying(true);
      const { data } = await axios.post(
        `${API_ENDPOINT}user/set-password?id=${id}`,
        { password, confPassword: confirmPassword }
      );
      setResetSuccess(true);
      toast.success(data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setVerifying(false);
    }
  };
  if (verificationError) {
    return <Alert severity="error">{verificationError}</Alert>;
  }

  return resetSuccess ? (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh" // Full viewport height
    >
      <Alert severity="success">
        <AlertTitle>Password Reset Successful!</AlertTitle>
        Your password has been reset successfully. You can now log in to the
        BetaZen App using your new password.
      </Alert>
    </Box>
  ) : (
    <Container maxWidth="sm">
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          marginTop: 5,
          textAlign: "center",
          borderRadius: 2,
        }}
      >
        {/* App Logo */}
        <Box display="flex" justifyContent="center" mb={2}>
          <img src={APP_LOGO_192} alt="BetaZen Logo" width={120} />
        </Box>

        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Set Your Password
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Create a strong password for your BetaZen account.
        </Typography>

        {/* Password Form */}
        <form onSubmit={handleSubmit}>
          <TextField
            size="small"
            label="New Password"
            variant="outlined"
            fullWidth
            type={showPassword ? "text" : "password"}
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handlePasswordToggle} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            size="small"
            label="Confirm Password"
            variant="outlined"
            fullWidth
            type={showPassword ? "text" : "password"}
            margin="normal"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          {/* Error Message */}
          {error && (
            <Typography color="error" variant="body2" mt={1}>
              {error}
            </Typography>
          )}

          {/* Submit Button */}
          <LoadingButton
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mt: 3, py: 1.2 }}
            loading={verifying}
          >
            Set Password
          </LoadingButton>
        </form>
      </Paper>
    </Container>
  );
};

export default PasswordSetPage;
